/**
 * DELETE JOB confirmation dialog.
 *
 * ⚠️ This dialog is NOT the Cancel Job / Abort workflow. It never routes
 * through cancellation logic, never creates an abort and never charges the
 * customer. It calls the dedicated server-side delete workflow only.
 */
import { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { deleteJobPermanently } from "@/lib/deleteJob";

export interface DeleteJobTarget {
  id: string;
  job_reference?: string | null;
  vehicle_registration?: string | null;
  status?: string | null;
  collection_address?: string | null;
  delivery_address?: string | null;
  customerName?: string | null;
}

interface DeleteJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: DeleteJobTarget | null;
  /** Called after a successful delete (or when the job no longer exists). */
  onDeleted: (jobId: string) => void;
  /** Surface the result message to the user (toast helper from the caller). */
  notify: (message: string, kind: "success" | "error") => void;
}

export function DeleteJobDialog({ open, onOpenChange, job, onDeleted, notify }: DeleteJobDialogProps) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!job || deleting) return;
    setDeleting(true);
    try {
      const outcome = await deleteJobPermanently(job.id);
      if (outcome.success) {
        notify("Job deleted", "success");
        onDeleted(job.id);
      } else if (outcome.code === "JOB_NOT_FOUND") {
        // Idempotent: already gone — remove it from the view, no second workflow.
        notify(outcome.message, "success");
        onDeleted(job.id);
      } else {
        notify(outcome.message, "error");
      }
    } finally {
      setDeleting(false);
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-destructive" />
            Delete Job?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Are you sure you want to delete this job? This action removes the job from
                TransportMate and does not create an abort or customer charge.
              </p>
              {job && (
                <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
                  <div><span className="text-muted-foreground">Job reference:</span> <span className="font-medium">{job.job_reference || "—"}</span></div>
                  <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{job.customerName || "—"}</span></div>
                  <div><span className="text-muted-foreground">Vehicle:</span> <span className="font-medium">{job.vehicle_registration || "—"}</span></div>
                  <div><span className="text-muted-foreground">Route:</span> <span className="font-medium">{job.collection_address || "—"} → {job.delivery_address || "—"}</span></div>
                  <div><span className="text-muted-foreground">Status:</span> <span className="font-medium">{job.status || "—"}</span></div>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              void handleDelete();
            }}
            disabled={deleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleting ? "Deleting…" : "Delete Job"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
